import React, { useState, useEffect } from 'react';
import { fsGetCol } from '../../lib/firebase';
import { toLocalDateStr, fmtDayDate } from '../../lib/datetime';
import { resolvePickupAdult } from '../../lib/pickups';

// ─── CAREGIVER SCHEDULE TAB ───────────────────────────────────────────────────
// Reads the owner-published schedule snapshots (families/{id}/schedule/*) —
// NOT Google Calendar, which token viewers cannot access. Shows the next
// 7 days of events and pickups.
//
// `onlyAssignedTo` (a sitter id) narrows this to just that caregiver's own
// pickups/events — the "My Assigned Events" scope for caregivers (grandparents
// running carpool, etc.) who shouldn't see the whole family's week.

export default function ScheduleTab({ familyData, onlyAssignedTo = null }) {
  const [days, setDays] = useState(null); // null = loading

  const carePartners = familyData?.carePartners || [];
  const sitters      = familyData?.sitters || [];
  const myIdentifier = onlyAssignedTo
    ? sitters.find(s => s.id === onlyAssignedTo)?.identifier || null
    : null;

  useEffect(() => {
    if (!familyData?.id) { setDays([]); return; }
    const today = toLocalDateStr(new Date());
    fsGetCol(`families/${familyData.id}/schedule`, [
      { field: 'date', op: '>=', value: today },
    ])
      .then(docs => {
        const horizon = toLocalDateStr(new Date(Date.now() + 7 * 86400000));
        setDays((docs || [])
          .filter(d => d.date && d.date <= horizon)
          .sort((a, b) => a.date.localeCompare(b.date)));
      })
      .catch(() => setDays([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyData?.id]);

  function personName(identifier) {
    if (!identifier) return null;
    return resolvePickupAdult(identifier, { carePartners, sitters });
  }

  if (days === null) return (
    <div style={{ padding: 32 }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
  );

  if (!days.length) return (
    <div className="empty-state" style={{ padding: 32, textAlign: 'center' }}>
      No schedule shared yet — ask the family to publish it from Review & Assign.
    </div>
  );

  // Filtered to just this caregiver's own pickups/events; days with nothing
  // of theirs are dropped rather than shown empty, so the list stays short.
  const visibleDays = onlyAssignedTo
    ? days
        .map(day => ({
          ...day,
          pickups: (day.pickups || []).filter(p => p.adultId === myIdentifier || p.adultId === `cg:${onlyAssignedTo}`),
          events: (day.events || []).filter(e => e.assignee === myIdentifier),
        }))
        .filter(day => day.pickups.length || day.events.length)
    : days;

  if (onlyAssignedTo && !visibleDays.length) return (
    <div className="empty-state" style={{ padding: 32, textAlign: 'center' }}>
      Nothing assigned to you this week.
    </div>
  );

  const today = toLocalDateStr(new Date());
  const newestPublish = days.reduce((max, d) => (d.publishedAt > max ? d.publishedAt : max), '');

  return (
    <div className="schedule-list">
      {visibleDays.map(day => (
        <div key={day.date} className="schedule-day-group">
          <div className="schedule-day-group__header">
            {day.date === today ? 'Today' : fmtDayDate(day.date)}
          </div>

          {/* Pickups first — the thing a caregiver most needs to know */}
          {(day.pickups || []).map((p, pi) => (
            <div key={`p-${p.childId}-${p.leg || 'pickup'}-${pi}`} className="schedule-row schedule-row--pickup">
              <span className="schedule-row__time">{p.time}</span>
              <span className="schedule-row__title">
                {p.leg === 'dropoff' ? '🎒' : '🚗'} {p.label || 'School pickup'} · {p.childName}
                {p.source === 'override' && <span className="badge badge--override" style={{ marginLeft: 6 }}>changed</span>}
              </span>
              {p.adultId && <span className="schedule-row__person">{personName(p.adultId)}</span>}
            </div>
          ))}

          {(day.events || []).map((e, i) => (
            <div key={i} className="schedule-row">
              <span className="schedule-row__time">{e.allDay ? 'All day' : e.startTime}</span>
              <span className="schedule-row__title">{e.title}</span>
              {e.assignee && <span className="schedule-row__person">{personName(e.assignee) || e.assignee}</span>}
            </div>
          ))}

          {!(day.pickups || []).length && !(day.events || []).length && (
            <div className="schedule-row"><span className="schedule-row__title" style={{ color: 'var(--ink-muted)' }}>Nothing scheduled</span></div>
          )}
        </div>
      ))}
      {newestPublish && (
        <p style={{ fontSize: 11, color: 'var(--ink-muted)', textAlign: 'center', padding: '8px 0 16px' }}>
          Last updated {new Date(newestPublish).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </p>
      )}
    </div>
  );
}
